import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import { TextField, TextFieldProps } from "../TextField";
import { useDebounce } from "../utils/use-debounce";
import { SelectItemType } from "../Select";

const AutoCompleteWrapper = styled.div`
  position: relative;
`;

const ResultsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 99;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  border: 2px solid ${({ theme }) => theme.colors.charcoal};
  border-top: 0px;
  max-height: 250px;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  flex: 1;
`;

const Result = styled.button<{ isSelected?: boolean }>`
  display: inline-block;
  border: none;
  padding: 1rem 2rem;
  margin: 0;
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  color: ${({ theme }) => theme.colors.charcoal};
  font-family: sans-serif;
  cursor: pointer;
  width: 100%;
  text-align: initial;
  transition: background 250ms ease-in-out, transform 150ms ease;
  background-color: ${({ isSelected, theme }) =>
    isSelected && theme.colors.mist};
  -webkit-appearance: none;
  -moz-appearance: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.mist};
  }
`;

const AttributionFooter = styled.div`
  padding: 0.75rem 1rem;
  width: 100%;
  text-align: right;
  border-top: 1px solid ${({ theme }) => theme.colors.mist};
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const AutoCompleteTextField = styled(TextField)<{ isInvalid: boolean }>`
  input {
    margin-bottom: ${({ isInvalid }) => !isInvalid && "0px"} !important;
  }
`;

export type AutoCompleteFetchArgs = {
  query: string;
  limit: number;
};

export type AutoCompleteOptionType = {
  key: string;
  skipInputUpdate?: boolean;
} & SelectItemType;

type AutoCompleteProps = {
  input: string;
  onInputChange: (value: string) => void;

  selectedOption: {
    value: string;
    label: string;
  };
  fetchOptions: (
    args: AutoCompleteFetchArgs
  ) => Promise<AutoCompleteOptionType[]>;
  onSelectOption: (value: string) => void;
  noResultsMessage?: string;
  showGoogleAttribution?: boolean;
  blurOnSelect?: boolean;

  settings?: Partial<{
    fetchLimit: number;
    minSearchLength: number;
    debounceDelay: number;
  }>;
} & Omit<TextFieldProps, "value">; // Value is controlled by the AutoComplete (input prop)

export const AutoComplete = ({
  input,
  onInputChange,
  selectedOption,
  fetchOptions,
  onSelectOption,
  noResultsMessage,
  showGoogleAttribution,
  blurOnSelect = false,
  settings = {},
  ...props
}: AutoCompleteProps) => {
  const { fetchLimit, minSearchLength, debounceDelay } = settings;

  const [results, setResults] = useState<AutoCompleteOptionType[]>([]);
  const [error, setError] = useState<string>();
  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const hasSelectedRef = useRef(false);

  const debouncedQuery: string = useDebounce(
    input || selectedOption.label,
    debounceDelay || 500
  );

  useEffect(() => {
    const fetchData = async () => {
      const resultsData = await fetchOptions({
        query: debouncedQuery,
        limit: fetchLimit || 10,
      }).catch((error) => {
        console.error(error);
        return undefined;
      });

      if (!resultsData) {
        setResults([]);
        setError("Could not fetch results");
        return;
      }

      if (resultsData.length > 0) {
        setResults(resultsData);
        setError(undefined);
        autoCompleteRef.current?.focus();
      } else {
        setResults([]);
        if (noResultsMessage) {
          setError(noResultsMessage);
        }
      }
    };

    if (hasSelectedRef.current) {
      hasSelectedRef.current = false;
      setResults([]);
      setError(undefined);
      return;
    }

    if (
      debouncedQuery === selectedOption.label ||
      debouncedQuery?.length < (minSearchLength || 3)
    ) {
      setResults([]);
      setError(undefined);
    } else {
      fetchData();
    }
  }, [
    debouncedQuery,
    selectedOption,
    fetchOptions,
    fetchLimit,
    minSearchLength,
  ]);

  return (
    <AutoCompleteWrapper>
      <AutoComplete.TextField
        {...props}
        ref={autoCompleteRef}
        value={input}
        onChange={(event) => {
          onInputChange(event.target.value);
        }}
        onBlur={() => {
          setResults([]);
        }}
        errorText={props.errorText || error}
        isInvalid={props.isInvalid || !!error || false}
        autoComplete="off"
      />

      {results.length > 0 && (
        <ResultsContainer>
          <ResultsList role="listbox" aria-label="Address suggestions">
            {results.map(({ key, value, label, skipInputUpdate }) => (
              <Result
                key={`result_${key}`}
                role="option"
                aria-selected={selectedOption.value === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (selectedOption.value !== value) {
                    hasSelectedRef.current = true;
                    onSelectOption(value);
                    if (!skipInputUpdate) {
                      onInputChange(label);
                    }
                  }
                  setResults([]);
                  if (blurOnSelect) {
                    autoCompleteRef.current?.blur();
                  }
                }}
                isSelected={selectedOption.value === value}
                data-testid={`autocomplete-result-${key}`}
              >
                <Typography use="body1" as="p">
                  {label}
                </Typography>
              </Result>
            ))}
          </ResultsList>
          {showGoogleAttribution && (
            <AttributionFooter>Powered by Google Maps</AttributionFooter>
          )}
        </ResultsContainer>
      )}
    </AutoCompleteWrapper>
  );
};

AutoComplete.TextField = AutoCompleteTextField;
